import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { useResourceList, useResourceMutations } from "@/hooks/use-admin-resource";
import { AdminPageHeader, RowActions, AdminEmptyState } from "@/components/admin/crud-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HOSTING_ICON_NAMES } from "@/lib/hosting-icons";
import { SortableList } from "@/components/admin/sortable-list";
import { MediaField } from "@/components/admin/media-picker";
import { LivePreviewFrame } from "@/components/admin/hosting/live-preview-frame";
import { RevisionsPanel } from "@/components/admin/hosting/revisions-panel";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { Rocket, Loader2, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/hosting-content")({
  component: HostingContentAdmin,
});

type Page = {
  id: string; slug: string; title: string; subtitle: string | null;
  hero_eyebrow: string | null; hero_cta_label: string | null; hero_cta_href: string | null;
  seo_title: string | null; seo_description: string | null; og_image_url: string | null;
  hero_image_url: string | null; hero_image_alt: string | null;
  enabled: boolean; sort_order: number; status: string; published_at: string | null;
};
type Plan = {
  id: string; page_slug: string; name: string; badge: string | null; description: string | null;
  price_monthly_cents: number; price_yearly_cents: number; price_triennial_cents: number;
  currency: string; features: string[]; cta_label: string; cta_href: string;
  is_featured: boolean; enabled: boolean; sort_order: number; badge_image_url: string | null; status: string;
};
type Tile = { id: string; page_slug: string; icon: string; title: string; description: string | null; enabled: boolean; sort_order: number; image_url: string | null; image_alt: string | null; status: string };
type Row = { id: string; page_slug: string; label: string; values: Record<string, string>; sort_order: number; highlight_image_url: string | null; enabled: boolean; status: string };
type Faq = { id: string; page_slug: string; question: string; answer: string; enabled: boolean; sort_order: number; image_url: string | null; status: string };

function HostingContentAdmin() {
  const qc = useQueryClient();
  const { data: pages, isLoading } = useResourceList<Page>("hosting_pages", "sort_order", true);
  const [slug, setSlug] = useState<string | null>(null);
  const [tab, setTab] = useState<"page" | "plans" | "tiles" | "compare" | "faqs" | "history">("page");
  const [refreshKey, setRefreshKey] = useState(0);

  const active = useMemo(() => (pages ?? []).find((p) => p.slug === slug) ?? pages?.[0] ?? null, [pages, slug]);
  const activeSlug = active?.slug ?? null;

  const refetchAll = () => {
    ["hosting_pages","hosting_plans","hosting_feature_tiles","hosting_comparison_rows","hosting_faqs","hosting_revisions"].forEach((t) =>
      qc.invalidateQueries({ queryKey: [t, "list"] })
    );
    qc.invalidateQueries({ queryKey: ["hosting-cms"] });
    qc.invalidateQueries({ queryKey: ["hosting_revisions"] });
    setRefreshKey((k) => k + 1);
  };

  const publish = useMutation({
    mutationFn: async (s: string) => {
      const { error } = await supabase.rpc("publish_hosting_page", { _slug: s, _note: undefined });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Page published live"); refetchAll(); },
    onError: (e: any) => toast.error(e.message ?? "Publish failed"),
  });

  const hasDraft = active?.status === "draft";
  useUnsavedChangesGuard(hasDraft);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Hosting pages CMS"
        description="Edit content on the left, preview on the right. Publish snapshots you can roll back to."
        count={pages?.length ?? 0}
        countLabel="pages"
      />

      <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)_minmax(0,1fr)]">
        <PagesSidebar pages={pages ?? []} isLoading={isLoading} activeSlug={activeSlug} onSelect={setSlug} onChange={refetchAll} />

        <div className="min-w-0">
          {!active ? (
            <AdminEmptyState title="No page selected" description="Create or select a hosting page on the left to start editing." />
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2">
                <div className="text-xs text-muted-foreground">
                  Editing <code className="font-mono text-foreground">/{active.slug}</code>
                  {active.published_at ? (
                    <> · last published {formatDistanceToNow(new Date(active.published_at), { addSuffix: true })}</>
                  ) : (
                    <> · never published</>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={active.status === "published" ? "default" : "secondary"} className="text-[10px]">
                    {active.status === "draft" ? "Draft changes" : "Published"}
                  </Badge>
                  <Button size="sm" disabled={publish.isPending} onClick={() => publish.mutate(active.slug)}>
                    {publish.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Rocket className="mr-1 h-3 w-3" />}
                    Publish
                  </Button>
                </div>
              </div>

              {hasDraft && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>You have unpublished draft changes. Visitors still see the last published snapshot until you press <strong>Publish</strong>.</span>
                </div>
              )}

              <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
                <PillTabsList>
                  <PillTabsTrigger value="page">Page & SEO</PillTabsTrigger>
                  <PillTabsTrigger value="plans">Plans</PillTabsTrigger>
                  <PillTabsTrigger value="tiles">Tiles</PillTabsTrigger>
                  <PillTabsTrigger value="compare">Comparison</PillTabsTrigger>
                  <PillTabsTrigger value="faqs">FAQs</PillTabsTrigger>
                  <PillTabsTrigger value="history">History</PillTabsTrigger>
                </PillTabsList>
                <TabsContent value="page" className="pt-4"><PageEditor page={active} onChange={refetchAll} /></TabsContent>
                <TabsContent value="plans" className="pt-4"><PlansEditor slug={active.slug} onChange={refetchAll} /></TabsContent>
                <TabsContent value="tiles" className="pt-4"><TilesEditor slug={active.slug} onChange={refetchAll} /></TabsContent>
                <TabsContent value="compare" className="pt-4"><CompareEditor slug={active.slug} onChange={refetchAll} /></TabsContent>
                <TabsContent value="faqs" className="pt-4"><FaqsEditor slug={active.slug} onChange={refetchAll} /></TabsContent>
                <TabsContent value="history" className="pt-4"><RevisionsPanel slug={active.slug} onRestored={refetchAll} /></TabsContent>
              </Tabs>
            </div>
          )}
        </div>

        <div className="hidden min-w-0 lg:block">
          {active && <LivePreviewFrame slug={active.slug} refreshKey={refreshKey} />}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Sidebar (with DnD) -------------------- */

function PagesSidebar({ pages, isLoading, activeSlug, onSelect, onChange }: {
  pages: Page[]; isLoading: boolean; activeSlug: string | null; onSelect: (slug: string) => void; onChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Page | null>(null);
  const { create, update, remove } = useResourceMutations("hosting_pages");
  const reorder = useReorder("hosting_pages", onChange);

  return (
    <aside className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border p-3">
        <div className="text-sm font-semibold">Pages</div>
        <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }}>+ New</Button>
      </div>
      <div className="max-h-[70vh] overflow-auto p-2">
        {isLoading && <div className="p-2 text-xs text-muted-foreground">Loading…</div>}
        <SortableList
          items={pages}
          onReorder={(ids) => reorder.mutate(ids)}
          renderItem={(p, handle) => (
            <div className={`group flex items-center gap-1 rounded-md px-2 py-1.5 text-sm hover:bg-accent ${activeSlug === p.slug ? "bg-accent" : ""}`}>
              {handle}
              <button onClick={() => onSelect(p.slug)} className="min-w-0 flex-1 text-left">
                <div className="truncate font-medium">{p.title}</div>
                <div className="truncate text-[10px] text-muted-foreground">/{p.slug}</div>
              </button>
              <Badge variant={p.enabled ? "default" : "secondary"} className="text-[9px]">{p.enabled ? "On" : "Off"}</Badge>
            </div>
          )}
        />
      </div>
      <PageMetaDialog
        key={editing?.id ?? "new"} open={open} onOpenChange={setOpen} editing={editing}
        onDelete={editing ? () => { if (confirm("Delete this page and ALL its content?")) remove.mutate(editing.id, { onSuccess: () => { setOpen(false); onChange(); } }); } : undefined}
        onSubmit={(v) => {
          if (editing) update.mutate({ id: editing.id, values: v }, { onSuccess: () => { setOpen(false); onChange(); } });
          else create.mutate(v, { onSuccess: () => { setOpen(false); onChange(); } });
        }}
      />
    </aside>
  );
}

/* -------------------- Page editor -------------------- */

function PageEditor({ page, onChange }: { page: Page; onChange: () => void }) {
  const { update } = useResourceMutations("hosting_pages");
  const [form, setForm] = useState<Page>(page);
  useEffect(() => setForm(page), [page.id]);

  const save = (patch: Partial<Page>) => update.mutate(
    { id: page.id, values: { ...patch, status: "draft" } },
    { onSuccess: () => onChange() }
  );

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Slug (URL segment)"><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="hosting/web" /></Field>
        <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
        <Field label="Hero eyebrow"><Input value={form.hero_eyebrow ?? ""} onChange={(e) => setForm({ ...form, hero_eyebrow: e.target.value })} /></Field>
        <Field label="Sort order"><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} /></Field>
      </div>
      <Field label="Subtitle"><Textarea rows={2} value={form.subtitle ?? ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Hero CTA label"><Input value={form.hero_cta_label ?? ""} onChange={(e) => setForm({ ...form, hero_cta_label: e.target.value })} /></Field>
        <Field label="Hero CTA link"><Input value={form.hero_cta_href ?? ""} onChange={(e) => setForm({ ...form, hero_cta_href: e.target.value })} placeholder="#plans" /></Field>
      </div>
      <MediaField label="Hero image" value={form.hero_image_url} onChange={(v) => setForm({ ...form, hero_image_url: v })} />
      <Field label="Hero image alt text"><Input value={form.hero_image_alt ?? ""} onChange={(e) => setForm({ ...form, hero_image_alt: e.target.value })} /></Field>

      <div className="border-t border-border pt-4">
        <div className="mb-3 text-sm font-semibold">SEO</div>
        <div className="grid gap-3">
          <Field label="SEO title"><Input value={form.seo_title ?? ""} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} /></Field>
          <Field label="Meta description"><Textarea rows={2} value={form.seo_description ?? ""} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} /></Field>
          <MediaField label="Open Graph image" value={form.og_image_url} onChange={(v) => setForm({ ...form, og_image_url: v })} />
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-border pt-4">
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} /> Enabled (visible on site)
        </label>
        <Button onClick={() => save(form)}>Save draft</Button>
      </div>
    </div>
  );
}

function PageMetaDialog({ open, onOpenChange, editing, onSubmit, onDelete }: {
  open: boolean; onOpenChange: (v: boolean) => void; editing: Page | null;
  onSubmit: (v: Partial<Page>) => void; onDelete?: () => void;
}) {
  const [slug, setSlug] = useState(editing?.slug ?? "");
  const [title, setTitle] = useState(editing?.title ?? "");
  const [enabled, setEnabled] = useState(editing?.enabled ?? true);
  const [order, setOrder] = useState<number>(editing?.sort_order ?? 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit page" : "New hosting page"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <Field label="Slug"><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="hosting/wordpress" /></Field>
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Sort order"><Input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} /></Field>
            <label className="flex items-end gap-2 pb-1 text-sm"><Switch checked={enabled} onCheckedChange={setEnabled} /> Enabled</label>
          </div>
        </div>
        <DialogFooter className="flex justify-between">
          {onDelete && <Button variant="destructive" onClick={onDelete}>Delete</Button>}
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={() => onSubmit({ slug, title, enabled, sort_order: order })}>{editing ? "Save" : "Create"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Plans -------------------- */

function PlansEditor({ slug, onChange }: { slug: string; onChange: () => void }) {
  const { data } = useResourceList<Plan>("hosting_plans", { orderBy: "sort_order", ascending: true, filter: [{ column: "page_slug", value: slug }], key: slug });
  const { create, update, remove } = useResourceMutations("hosting_plans");
  const reorder = useReorder("hosting_plans", onChange);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button size="sm" onClick={() => { setEditing(null); setOpen(true); }}>+ New plan</Button></div>
      {(data ?? []).length === 0 ? <AdminEmptyState title="No plans yet" description="Add your first pricing card." /> : (
        <SortableList
          className="space-y-2"
          items={data ?? []}
          onReorder={(ids) => reorder.mutate(ids)}
          renderItem={(p, handle) => (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-3">
              {handle}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {p.name}
                  {p.is_featured && <Badge className="text-[9px]">★ Featured</Badge>}
                  {p.badge && <Badge variant="secondary" className="text-[9px]">{p.badge}</Badge>}
                  {p.status === "draft" && <Badge variant="outline" className="text-[9px]">Draft</Badge>}
                  <Badge variant={p.enabled ? "default" : "secondary"} className="text-[9px]">{p.enabled ? "On" : "Off"}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {p.currency} {(p.price_monthly_cents/100).toFixed(2)}/mo · {(p.price_yearly_cents/100).toFixed(2)}/yr · {(p.price_triennial_cents/100).toFixed(2)}/3yr
                </div>
              </div>
              <RowActions onEdit={() => { setEditing(p); setOpen(true); }} onDelete={() => { if (confirm("Delete plan?")) remove.mutate(p.id, { onSuccess: onChange }); }} />
            </div>
          )}
        />
      )}
      <PlanDialog key={editing?.id ?? "new"} open={open} onOpenChange={setOpen} editing={editing} slug={slug}
        onSubmit={(v) => {
          if (editing) update.mutate({ id: editing.id, values: { ...v, status: "draft" } }, { onSuccess: () => { setOpen(false); onChange(); } });
          else create.mutate({ ...v, page_slug: slug, status: "draft" }, { onSuccess: () => { setOpen(false); onChange(); } });
        }} />
    </div>
  );
}

function PlanDialog({ open, onOpenChange, editing, slug, onSubmit }: {
  open: boolean; onOpenChange: (v: boolean) => void; editing: Plan | null; slug: string;
  onSubmit: (v: Partial<Plan>) => void;
}) {
  const [name, setName] = useState(editing?.name ?? "");
  const [badge, setBadge] = useState(editing?.badge ?? "");
  const [badgeImg, setBadgeImg] = useState(editing?.badge_image_url ?? null);
  const [description, setDescription] = useState(editing?.description ?? "");
  const [monthly, setMonthly] = useState(((editing?.price_monthly_cents ?? 0) / 100).toString());
  const [yearly, setYearly] = useState(((editing?.price_yearly_cents ?? 0) / 100).toString());
  const [triennial, setTriennial] = useState(((editing?.price_triennial_cents ?? 0) / 100).toString());
  const [currency, setCurrency] = useState(editing?.currency ?? "USD");
  const [features, setFeatures] = useState((editing?.features ?? []).join("\n"));
  const [ctaLabel, setCtaLabel] = useState(editing?.cta_label ?? "Get started");
  const [ctaHref, setCtaHref] = useState(editing?.cta_href ?? "#");
  const [featured, setFeatured] = useState(editing?.is_featured ?? false);
  const [enabled, setEnabled] = useState(editing?.enabled ?? true);
  const [order, setOrder] = useState<number>(editing?.sort_order ?? 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{editing ? "Edit plan" : "New plan"} — {slug}</DialogTitle></DialogHeader>
        <div className="grid max-h-[70vh] gap-3 overflow-auto py-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Badge (optional)"><Input value={badge ?? ""} onChange={(e) => setBadge(e.target.value)} placeholder="Most popular" /></Field>
          </div>
          <MediaField label="Badge image (optional)" value={badgeImg} onChange={setBadgeImg} />
          <Field label="Short description"><Textarea rows={2} value={description ?? ""} onChange={(e) => setDescription(e.target.value)} /></Field>
          <div className="grid gap-3 sm:grid-cols-4">
            <Field label="Monthly"><Input type="number" step="0.01" value={monthly} onChange={(e) => setMonthly(e.target.value)} /></Field>
            <Field label="Yearly / mo"><Input type="number" step="0.01" value={yearly} onChange={(e) => setYearly(e.target.value)} /></Field>
            <Field label="3-year / mo"><Input type="number" step="0.01" value={triennial} onChange={(e) => setTriennial(e.target.value)} /></Field>
            <Field label="Currency"><Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} /></Field>
          </div>
          <Field label="Features (one per line)"><Textarea rows={6} value={features} onChange={(e) => setFeatures(e.target.value)} placeholder={"1 Website\n10 GB SSD Storage\nFree SSL"} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="CTA label"><Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} /></Field>
            <Field label="CTA link"><Input value={ctaHref} onChange={(e) => setCtaHref(e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-3 items-center gap-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={featured} onCheckedChange={setFeatured} /> Featured</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={enabled} onCheckedChange={setEnabled} /> Enabled</label>
            <Field label="Sort"><Input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({
            name, badge: badge || null, badge_image_url: badgeImg, description: description || null,
            price_monthly_cents: Math.round(Number(monthly) * 100),
            price_yearly_cents: Math.round(Number(yearly) * 100),
            price_triennial_cents: Math.round(Number(triennial) * 100),
            currency, features: features.split("\n").map((s) => s.trim()).filter(Boolean),
            cta_label: ctaLabel, cta_href: ctaHref, is_featured: featured, enabled, sort_order: order,
          })}>{editing ? "Save draft" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Tiles -------------------- */

function TilesEditor({ slug, onChange }: { slug: string; onChange: () => void }) {
  const { data } = useResourceList<Tile>("hosting_feature_tiles", { orderBy: "sort_order", ascending: true, filter: [{ column: "page_slug", value: slug }], key: slug });
  const { create, update, remove } = useResourceMutations("hosting_feature_tiles");
  const reorder = useReorder("hosting_feature_tiles", onChange);
  const [editing, setEditing] = useState<Tile | null>(null);
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button size="sm" onClick={() => { setEditing(null); setOpen(true); }}>+ New tile</Button></div>
      {(data ?? []).length === 0 ? <AdminEmptyState title="No feature tiles yet" description="Add a tile for the performance grid." /> : (
        <SortableList
          className="space-y-2"
          items={data ?? []}
          onReorder={(ids) => reorder.mutate(ids)}
          renderItem={(t, handle) => (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-3">
              {handle}
              {t.image_url ? <img src={t.image_url} alt="" className="h-10 w-10 rounded object-cover" /> : <code className="rounded bg-muted px-2 py-1 text-[10px]">{t.icon}</code>}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{t.title}</div>
                <div className="truncate text-xs text-muted-foreground">{t.description}</div>
              </div>
              {t.status === "draft" && <Badge variant="outline" className="text-[9px]">Draft</Badge>}
              <Badge variant={t.enabled ? "default" : "secondary"} className="text-[9px]">{t.enabled ? "On" : "Off"}</Badge>
              <RowActions onEdit={() => { setEditing(t); setOpen(true); }} onDelete={() => { if (confirm("Delete tile?")) remove.mutate(t.id, { onSuccess: onChange }); }} />
            </div>
          )}
        />
      )}
      <TileDialog key={editing?.id ?? "new"} open={open} onOpenChange={setOpen} editing={editing}
        onSubmit={(v) => {
          if (editing) update.mutate({ id: editing.id, values: { ...v, status: "draft" } }, { onSuccess: () => { setOpen(false); onChange(); } });
          else create.mutate({ ...v, page_slug: slug, status: "draft" }, { onSuccess: () => { setOpen(false); onChange(); } });
        }} />
    </div>
  );
}

function TileDialog({ open, onOpenChange, editing, onSubmit }: {
  open: boolean; onOpenChange: (v: boolean) => void; editing: Tile | null;
  onSubmit: (v: Partial<Tile>) => void;
}) {
  const [icon, setIcon] = useState(editing?.icon ?? "Sparkles");
  const [image, setImage] = useState(editing?.image_url ?? null);
  const [imageAlt, setImageAlt] = useState(editing?.image_alt ?? "");
  const [title, setTitle] = useState(editing?.title ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [enabled, setEnabled] = useState(editing?.enabled ?? true);
  const [order, setOrder] = useState<number>(editing?.sort_order ?? 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit tile" : "New tile"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <Field label="Icon (used when no image)">
            <Select value={icon} onValueChange={setIcon}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                {HOSTING_ICON_NAMES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <MediaField label="Image (optional — overrides icon)" value={image} onChange={setImage} />
          {image && <Field label="Image alt text"><Input value={imageAlt ?? ""} onChange={(e) => setImageAlt(e.target.value)} /></Field>}
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label="Description"><Textarea rows={3} value={description ?? ""} onChange={(e) => setDescription(e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={enabled} onCheckedChange={setEnabled} /> Enabled</label>
            <Field label="Sort"><Input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({ icon, image_url: image, image_alt: imageAlt || null, title, description: description || null, enabled, sort_order: order })}>{editing ? "Save draft" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Comparison -------------------- */

function CompareEditor({ slug, onChange }: { slug: string; onChange: () => void }) {
  const { data: rows } = useResourceList<Row>("hosting_comparison_rows", { orderBy: "sort_order", ascending: true, filter: [{ column: "page_slug", value: slug }], key: slug });
  const { data: plans } = useResourceList<Plan>("hosting_plans", { orderBy: "sort_order", ascending: true, filter: [{ column: "page_slug", value: slug }], key: slug });
  const { create, update, remove } = useResourceMutations("hosting_comparison_rows");
  const reorder = useReorder("hosting_comparison_rows", onChange);
  const [editing, setEditing] = useState<Row | null>(null);
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button size="sm" onClick={() => { setEditing(null); setOpen(true); }}>+ New row</Button></div>
      {(!plans || plans.length === 0) ? <AdminEmptyState title="Add plans first" description="Comparison rows use plan names as columns." /> : (rows ?? []).length === 0 ? (
        <AdminEmptyState title="No rows yet" description="Add spec rows for the comparison table." />
      ) : (
        <SortableList
          className="space-y-2"
          items={rows ?? []}
          onReorder={(ids) => reorder.mutate(ids)}
          renderItem={(r, handle) => (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-3">
              {handle}
              {r.highlight_image_url && <img src={r.highlight_image_url} alt="" className="h-8 w-8 rounded object-cover" />}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{r.label}</div>
                <div className="truncate text-xs text-muted-foreground">{(plans ?? []).map((p) => `${p.name}: ${r.values?.[p.name] ?? "—"}`).join(" · ")}</div>
              </div>
              {r.status === "draft" && <Badge variant="outline" className="text-[9px]">Draft</Badge>}
              <RowActions onEdit={() => { setEditing(r); setOpen(true); }} onDelete={() => { if (confirm("Delete row?")) remove.mutate(r.id, { onSuccess: onChange }); }} />
            </div>
          )}
        />
      )}
      <RowDialog key={editing?.id ?? "new"} open={open} onOpenChange={setOpen} editing={editing} plans={plans ?? []}
        onSubmit={(v) => {
          if (editing) update.mutate({ id: editing.id, values: { ...v, status: "draft" } }, { onSuccess: () => { setOpen(false); onChange(); } });
          else create.mutate({ ...v, page_slug: slug, status: "draft" }, { onSuccess: () => { setOpen(false); onChange(); } });
        }} />
    </div>
  );
}

function RowDialog({ open, onOpenChange, editing, plans, onSubmit }: {
  open: boolean; onOpenChange: (v: boolean) => void; editing: Row | null; plans: Plan[];
  onSubmit: (v: Partial<Row>) => void;
}) {
  const [label, setLabel] = useState(editing?.label ?? "");
  const [highlight, setHighlight] = useState(editing?.highlight_image_url ?? null);
  const [order, setOrder] = useState<number>(editing?.sort_order ?? 0);
  const [values, setValues] = useState<Record<string, string>>(editing?.values ?? {});
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit row" : "New row"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <Field label="Feature label"><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Websites" /></Field>
          <MediaField label="Highlight image (optional)" value={highlight} onChange={setHighlight} />
          <div className="rounded-md border border-border p-3">
            <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Values per plan</div>
            <div className="grid gap-2">
              {plans.map((p) => (
                <div key={p.id} className="grid grid-cols-[110px_1fr] items-center gap-2">
                  <span className="truncate text-sm font-medium">{p.name}</span>
                  <Input value={values[p.name] ?? ""} onChange={(e) => setValues({ ...values, [p.name]: e.target.value })} placeholder="e.g. Unlimited" />
                </div>
              ))}
            </div>
          </div>
          <Field label="Sort"><Input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} /></Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({ label, values, sort_order: order, highlight_image_url: highlight })}>{editing ? "Save draft" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- FAQs -------------------- */

function FaqsEditor({ slug, onChange }: { slug: string; onChange: () => void }) {
  const { data } = useResourceList<Faq>("hosting_faqs", { orderBy: "sort_order", ascending: true, filter: [{ column: "page_slug", value: slug }], key: slug });
  const { create, update, remove } = useResourceMutations("hosting_faqs");
  const reorder = useReorder("hosting_faqs", onChange);
  const [editing, setEditing] = useState<Faq | null>(null);
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button size="sm" onClick={() => { setEditing(null); setOpen(true); }}>+ New FAQ</Button></div>
      {(data ?? []).length === 0 ? <AdminEmptyState title="No FAQs yet" description="Add common questions for this page." /> : (
        <SortableList
          className="space-y-2"
          items={data ?? []}
          onReorder={(ids) => reorder.mutate(ids)}
          renderItem={(f, handle) => (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-3">
              {handle}
              {f.image_url && <img src={f.image_url} alt="" className="h-8 w-8 rounded object-cover" />}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{f.question}</div>
                <div className="truncate text-xs text-muted-foreground">{f.answer}</div>
              </div>
              {f.status === "draft" && <Badge variant="outline" className="text-[9px]">Draft</Badge>}
              <Badge variant={f.enabled ? "default" : "secondary"} className="text-[9px]">{f.enabled ? "On" : "Off"}</Badge>
              <RowActions onEdit={() => { setEditing(f); setOpen(true); }} onDelete={() => { if (confirm("Delete FAQ?")) remove.mutate(f.id, { onSuccess: onChange }); }} />
            </div>
          )}
        />
      )}
      <FaqDialog key={editing?.id ?? "new"} open={open} onOpenChange={setOpen} editing={editing}
        onSubmit={(v) => {
          if (editing) update.mutate({ id: editing.id, values: { ...v, status: "draft" } }, { onSuccess: () => { setOpen(false); onChange(); } });
          else create.mutate({ ...v, page_slug: slug, status: "draft" }, { onSuccess: () => { setOpen(false); onChange(); } });
        }} />
    </div>
  );
}

function FaqDialog({ open, onOpenChange, editing, onSubmit }: {
  open: boolean; onOpenChange: (v: boolean) => void; editing: Faq | null;
  onSubmit: (v: Partial<Faq>) => void;
}) {
  const [question, setQuestion] = useState(editing?.question ?? "");
  const [answer, setAnswer] = useState(editing?.answer ?? "");
  const [image, setImage] = useState(editing?.image_url ?? null);
  const [enabled, setEnabled] = useState(editing?.enabled ?? true);
  const [order, setOrder] = useState<number>(editing?.sort_order ?? 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit FAQ" : "New FAQ"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <Field label="Question"><Input value={question} onChange={(e) => setQuestion(e.target.value)} /></Field>
          <Field label="Answer"><Textarea rows={5} value={answer} onChange={(e) => setAnswer(e.target.value)} /></Field>
          <MediaField label="Thumbnail (optional)" value={image} onChange={setImage} />
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={enabled} onCheckedChange={setEnabled} /> Enabled</label>
            <Field label="Sort"><Input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({ question, answer, image_url: image, enabled, sort_order: order })}>{editing ? "Save draft" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- shared -------------------- */

function useReorder(table: string, onChange: () => void) {
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.rpc("reorder_hosting_rows", { _table: table, _ids: ids });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Reordered"); onChange(); },
    onError: (e: any) => toast.error(e.message ?? "Reorder failed"),
  });
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="grid gap-1.5"><Label className="text-xs font-semibold uppercase text-muted-foreground">{label}</Label>{children}</div>;
}
